import React, { ReactNode } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { Classes, FormGroup as BluePrintFormGroup, IFormGroupProps } from '@blueprintjs/core';
import { TLCode } from '../../../common/enums';
import * as glamor from 'glamor';
import classNames from 'classnames';

export interface CommonFormGroupProps extends IFormGroupProps {
  renderLabel?: (label: string | ReactNode) => string | JSX.Element;
  label?: TLCode | string;

  placeholder?: TLCode;
  fill?: boolean;
  helperText?: TLCode;
  centerLabel?: boolean;
}

interface FormGroupProps extends CommonFormGroupProps, InjectedIntlProps {
  children?: React.ReactNode | React.ReactNode[];
}

const FormGroup = (props: FormGroupProps) => {
  const getTranslatedLabelText = (label?: TLCode | string) =>
    label !== undefined ? (typeof label === 'string' ? label : props.intl.formatMessage({ id: TLCode[label] })) : '';

  const getLabel = (getLabelProps: FormGroupProps) => {
    const { labelInfo, label, renderLabel, intl } = getLabelProps;
    const translatedLabel = getTranslatedLabelText(label);
    return renderLabel ? (
      renderLabel(translatedLabel)
    ) : (
      <span>
        {translatedLabel}
        &nbsp;
        {labelInfo && (
          <span className={Classes.TEXT_MUTED}>
            ({intl.formatMessage({ id: TLCode[TLCode.FORM_FIELD_REQUIRED_LABEL] })})
          </span>
        )}
      </span>
    );
  };

  const getHelperText = () => {
    const { helperText } = props;
    if (helperText) {
      return TLCode[helperText] ? getTranslatedLabelText(helperText as TLCode) : helperText;
    }
    return null;
  };

  const getFormGroupProps = (getFormGroupPropsProps: FormGroupProps) => {
    const { disabled, intent, inline, fill } = getFormGroupPropsProps;
    return {
      disabled,
      helperText: getHelperText(),
      intent,
      inline,
      fill,
      label: getLabel(props),
    };
  };

  const fillClassname = glamor.css({
    '& .bp3-form-content': {
      flex: '1',
      paddingRight: '1px',
    },
  });

  const inlineClassname = glamor.css({
    '& .bp3-label': {
      alignSelf: 'center',
    },
  });

  const labelBold = glamor.css({
    '& .bp3-label': {
      fontWeight: 'bold',
    },
  });

  const names = classNames(
    {
      [fillClassname.toString()]: props.fill,
      [inlineClassname.toString()]: props.centerLabel,
    },
    labelBold.toString(),
  );

  return (
    <BluePrintFormGroup {...getFormGroupProps(props)} className={names}>
      {props.children}
    </BluePrintFormGroup>
  );
};

export default injectIntl<CommonFormGroupProps>(FormGroup);
